import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { type AuthenticatedUser, login, type UserRole } from '@/api/auth'
import { ThemeToggle } from '@/components/theme-toggle'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { describeErrorMessage } from '@/lib/errors'
import { type LoginFormValues, loginSchema } from '@/lib/validation/auth'
import { useAuthStore } from '@/stores/auth'

export const Route = createFileRoute('/login')({
  component: LoginPage,
})

const ROLE_LANDING: Record<UserRole, string> = {
  seller: '/seller/dashboard',
  coach: '/coach',
  admin: '/admin',
}

function LoginPage() {
  const navigate = useNavigate()
  const setUser = useAuthStore((s) => s.setUser)
  const [serverMessage, setServerMessage] = useState<string | null>(null)

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { login: '', password: '' },
    mode: 'onSubmit',
  })

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: (user: AuthenticatedUser) => {
      setServerMessage(null)
      setUser(user)
      navigate({ to: ROLE_LANDING[user.role] })
    },
    onError: (err: Error) => {
      setServerMessage(describeErrorMessage(err, 'Не удалось войти.'))
    },
  })

  const onSubmit = (values: LoginFormValues) => {
    setServerMessage(null)
    mutation.mutate(values)
  }

  const loginError = form.formState.errors.login
  const passwordError = form.formState.errors.password

  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col items-center justify-center px-4 py-12">
      <div className="fixed top-3 right-3 z-10">
        <ThemeToggle />
      </div>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Вход</CardTitle>
          <CardDescription>Citilux Coach — кабинет продавца и тренера</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            noValidate
            className="flex flex-col gap-4"
            onSubmit={form.handleSubmit(onSubmit)}
            aria-busy={mutation.isPending || undefined}
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="login">Логин</Label>
              <Input
                id="login"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                aria-invalid={loginError !== undefined || undefined}
                aria-describedby={loginError ? 'login-error' : undefined}
                {...form.register('login')}
              />
              {loginError ? (
                <p id="login-error" role="alert" className="text-sm text-destructive">
                  {loginError.message}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="password">Пароль</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={passwordError !== undefined || undefined}
                aria-describedby={passwordError ? 'password-error' : undefined}
                {...form.register('password')}
              />
              {passwordError ? (
                <p id="password-error" role="alert" className="text-sm text-destructive">
                  {passwordError.message}
                </p>
              ) : null}
            </div>

            {serverMessage ? (
              <p role="alert" className="text-sm text-destructive">
                {serverMessage}
              </p>
            ) : null}

            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Вход…' : 'Войти'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  )
}
